import { Info } from '@mui/icons-material';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { getIvaCompact, getIvaFolderLink } from 'lib/api/business';
import CustomButton from 'lib/designSystem/Button';
import { showAlert } from 'lib/functions/utils';
import { PDFDocument } from 'pdf-lib';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

interface IVAFolderDownloaderProps {
  open: boolean;
  onClose: () => void;
}

export type IvaCompactResponse = {
  f22: { year: number; pdfBase64: string }[];
  f29: { year: number; month: number; pdfBase64: string }[];
};

const IVAFolderDownloader: React.FC<IVAFolderDownloaderProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [loadingFolder, setLoadingFolder] = useState(false);
  const [loadingCompact, setLoadingCompact] = useState(false);

  if (!id) return null;

  function downloadPdf(base64: string, fileName = 'documento.pdf') {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${base64}`;
    link.download = fileName;
    link.click();
  }

  /* eslint-disable no-await-in-loop */
  const mergePDFs = async (message: IvaCompactResponse) => {
    const mergedPdf = await PDFDocument.create();
    for (const pdfData of message.f29) {
      try {
        if (pdfData.pdfBase64) {
          const pdfBytes = Uint8Array.from(atob(pdfData.pdfBase64), (c) =>
            c.charCodeAt(0),
          );
          const pdf = await PDFDocument.load(pdfBytes);
          const copiedPages = await mergedPdf.copyPages(
            pdf,
            pdf.getPageIndices(),
          );
          copiedPages.forEach((page) => {
            mergedPdf.addPage(page);
          });
        }
      } catch (error) {
        console.error(error);
        console.error('Skipping pdf period:', pdfData.year, pdfData.month);
      }
    }

    for (const pdfData of message.f22) {
      if (pdfData.pdfBase64) {
        const pdfBytes = Uint8Array.from(atob(pdfData.pdfBase64), (c) =>
          c.charCodeAt(0),
        );
        const pdf = await PDFDocument.load(pdfBytes);
        const copiedPages = await mergedPdf.copyPages(
          pdf,
          pdf.getPageIndices(),
        );
        copiedPages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      }
    }

    const pdfDataUri = await mergedPdf.saveAsBase64({ dataUri: true });

    const a = document.createElement('a');
    a.href = pdfDataUri;
    a.download = `carpeta-tributaria-${id}.pdf`;
    a.click();
    URL.revokeObjectURL(pdfDataUri);
  };
  /* eslint-enable no-await-in-loop */

  const handleDownloadIvaFolder = async () => {
    setLoadingFolder(true);
    try {
      const data = await getIvaFolderLink(dispatch, id);
      downloadPdf(data.pdfBase64, data.filename);
    } catch (error) {
      console.error(error);
      showAlert(
        dispatch,
        'error',
        'No se pudo descargar la carpeta tributaria. Intenta nuevamente.',
      );
    } finally {
      setLoadingFolder(false);
    }
  };

  const handleDownloadIvaCompact = async () => {
    setLoadingCompact(true);
    try {
      const data = await getIvaCompact(dispatch, id);
      await mergePDFs(data);
    } catch (error) {
      console.error(error);
      showAlert(
        dispatch,
        'error',
        'No se pudo generar el compacto. Intenta nuevamente.',
      );
    } finally {
      setLoadingCompact(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Descargar Carpeta Tributaria</DialogTitle>
      <DialogContent>
        <div style={{ padding: '20px 0px' }}>
          <Typography variant="body1" gutterBottom>
            <strong>Importante:</strong> Este documento es generado directamente
            por el Servicio de Impuestos Internos (SII) y está sujeto a
            intermitencias e inconsistencias del mismo servicio.
          </Typography>
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px', position: 'relative' }}>
        <div
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Tooltip
            title="Método alternativo: Genera un compacto del F29 y F22. Busca cada documento por separado y los une en un PDF. Es posible que algunos de los documentos no se encuentren en el compacto debido a intermitencias del SII."
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'white',
                  color: 'black',
                  fontSize: '14px',
                  border: '1px solid #e0e0e0',
                  fontWeight: 300,
                  maxWidth: '300px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  padding: '10px',
                },
              },
            }}
          >
            <Info />
          </Tooltip>
          <CustomButton
            onClick={handleDownloadIvaCompact}
            color="secondary"
            size="small"
            disabled={loadingCompact}
            startIcon={loadingCompact ? <CircularProgress size={16} /> : null}
          >
            {loadingCompact ? 'Procesando...' : 'Compacto'}
          </CustomButton>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <CustomButton onClick={onClose} color="secondary">
            Atrás
          </CustomButton>
          <CustomButton
            onClick={handleDownloadIvaFolder}
            color="primary"
            disabled={loadingFolder}
            style={{ marginLeft: '8px' }}
            loading={loadingFolder}
          >
            {loadingFolder ? 'Descargando...' : 'Descargar'}
          </CustomButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default IVAFolderDownloader;
